"use client";

import Image from "next/image";
import Link from "next/link";
import type { FormEvent, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  BookOpen,
  Building2,
  Check,
  ChevronLeft,
  ChevronRight,
  Cloud,
  CircleCheck,
  CircleX,
  Cpu,
  Database,
  ExternalLink,
  FileText,
  FolderOpen,
  GraduationCap,
  HardDrive,
  Layers3,
  MapPinned,
  MessageCircle,
  Monitor,
  Pause,
  Play,
  PlayCircle,
  Quote,
  Route,
  Share2,
  ShieldCheck,
  Sprout,
  Users,
  Workflow,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { motion } from "motion/react";
import Autoplay from "embla-carousel-autoplay";

import { Badge } from "@workspace/ui/components/badge";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@workspace/ui/components/alert";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@workspace/ui/components/avatar";
import { Button } from "@workspace/ui/components/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@workspace/ui/components/carousel";
import type { CarouselApi } from "@workspace/ui/components/carousel";
import { Input } from "@workspace/ui/components/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@workspace/ui/components/card";
import { cn } from "@workspace/ui/lib/utils";
import type {
  AccountPlanFeature,
  AccountPlanModel,
} from "@/core/account-plan/domain/models";
import { buildSubscribePath, type PurchaseCycle } from "@/lib/purchase-flow";

import { MarketingShell } from "./MarketingShell";
import {
  platformJourneys,
  v2Gallery,
  v2Modules,
  v2ProofStats,
  v2Testimonials,
} from "./marketing-content";

const WHATSAPP_URL =
  "https://wa.me/5516988055633?text=Ol%C3%A1%2C%20quero%20conhecer%20os%20m%C3%B3dulos%20profissionais%20do%20GeoOffice%20V2.";
const YOUTUBE_URL = "https://www.youtube.com/@GeoOfficeV2";

const MARKETING_CTA_CLASS = "h-11 gap-2 px-5 text-sm";
const TRIAL_DESTINATION_PATH = "/account?destination=desktop%2Ftrial";

// Preencha estes caminhos quando a captura sanitizada estiver disponível.
const VIEWER_3D_VIDEO_SRC: string | null = null;
const VIEWER_3D_POSTER_SRC = "/images/viewer-3d-poster.jpg";

export function PublicHome() {
  return (
    <MarketingShell>
      <main id="top">
        <section className="relative isolate overflow-hidden border-b border-border">
          <TopographicBackdrop />
          <div className="mx-auto w-full max-w-7xl px-4 py-20 md:px-8 md:py-28 lg:px-10">
            <Reveal className="max-w-4xl">
              <Badge variant="outline" className="gap-2">
                <span className="size-1.5 rounded-full bg-success" />
                GeoOffice, desde 1999
              </Badge>
              <h1 className="cn-font-heading mt-7 max-w-4xl text-4xl font-semibold leading-[1.02] tracking-[-0.05em] md:text-6xl lg:text-7xl">
                Precisão para produzir. Liberdade para trabalhar online.
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-7 text-muted-foreground md:text-lg">
                O GeoOffice V2 é o software técnico para projetos e cálculos no
                Windows. O GeoOffice Platform é o ambiente Web para arquivos,
                aplicações e colaboração. Escolha um deles ou use os dois com a
                mesma conta.
              </p>
            </Reveal>
            <div className="mt-12 grid gap-4 lg:grid-cols-2">
              <ProductPortal
                icon={Monitor}
                eyebrow="Software para Windows"
                title="GeoOffice V2"
                description="Transforme dados de campo em projetos completos, com ferramentas para topografia, engenharia e agrimensura."
                features={[
                  "Georreferenciamento",
                  "Projetos",
                  "Estradas",
                  "Agricultura de precisão",
                ]}
                href="/desktop"
                cta="Conhecer o GeoOffice V2"
                image="/images/foto-trial7.jpg"
                imageAlt="Projetos viários no GeoOffice V2"
                product="v2"
              />
              <ProductPortal
                icon={Cloud}
                eyebrow="Produto Web"
                title="GeoOffice Platform"
                description="Organize arquivos, use aplicações técnicas e compartilhe seu trabalho em ambientes online."
                features={[
                  "Memorial SIGEF",
                  "Workspace",
                  "Utilitário ONR",
                  "Reconstituição",
                  "Cena 3D",
                  "Visualizador DWG/DXF",
                ]}
                href="/platform"
                cta="Conhecer o GeoOffice Platform"
                image="/images/foto-trial18.jpg"
                imageAlt="Visualização de trabalho técnico no GeoOffice Platform"
                product="platform"
              />
            </div>
          </div>
        </section>
        <section id="empresa" className="border-b border-border bg-muted/30">
          <div className="mx-auto grid w-full max-w-7xl gap-10 px-4 py-20 md:px-8 md:py-24 lg:grid-cols-[0.7fr_1.3fr] lg:items-center lg:px-10">
            <Reveal>
              <p className="font-mono text-5xl font-semibold tracking-[-0.06em] text-primary md:text-7xl">
                Desde 1999
              </p>
              <p className="mt-2 text-sm uppercase tracking-[0.18em] text-muted-foreground">
                conhecimento técnico em evolução
              </p>
            </Reveal>
            <Reveal delay={0.08}>
              <SectionHeading
                eyebrow="Solution Softwares"
                title="Conhecimento técnico que evolui com o trabalho."
                description="A Solution Softwares transforma experiência em engenharia, topografia e agrimensura em produtos especializados para produzir no Windows e trabalhar online. O GeoOffice V2 carrega essa trajetória. O GeoOffice Platform amplia o ecossistema para o navegador."
              />
            </Reveal>
          </div>
        </section>
        <section className="border-b border-border">
          <div className="mx-auto w-full max-w-7xl px-4 py-20 md:px-8 md:py-24 lg:px-10">
            <Reveal className="mx-auto max-w-3xl text-center">
              <SectionHeading
                eyebrow="Como os produtos se relacionam"
                title="Dois produtos independentes. Uma conta que aproxima os dois."
                description="Use apenas o produto que resolve sua necessidade hoje ou combine os dois na sua rotina."
                centered
              />
            </Reveal>
            <div className="mt-12 grid gap-4 md:grid-cols-2">
              <RelationshipPanel
                icon={Monitor}
                title="Projete com GeoOffice V2"
                description="Desenvolva desde uma simples Planta Planialtimétrica até uma Rodovia de alto padrão, com ferramentas bem direcionadas e um modo simples e preciso na aplicação."
                product="v2"
              />
              <RelationshipPanel
                icon={Cloud}
                title="Organize no GeoOffice Platform"
                description="Um ambiente independente em nuvem, preparado com ferramentas de engenharia e armazenamento para organizar e compartilhar seus projetos de forma segura."
              />
            </div>
            <Reveal delay={0.08} className="mx-auto mt-5 max-w-3xl">
              <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-4">
                <ShieldCheck className="mt-0.5 size-5 shrink-0 text-foreground" />
                <div>
                  <p className="text-sm font-semibold">
                    Um benefício concreto hoje
                  </p>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    Planos profissionais do GeoOffice V2 incluem benefícios
                    equivalentes ao plano Pessoal do GeoOffice Platform. A
                    sincronização automática entre projetos ainda está em
                    desenvolvimento.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
        <section className="border-b border-border bg-muted/30">
          <div className="mx-auto w-full max-w-7xl px-4 py-20 md:px-8 md:py-24 lg:px-10">
            <Reveal className="mx-auto max-w-3xl text-center">
              <SectionHeading
                eyebrow="Escolha seu universo"
                title="Comece pelo trabalho que precisa fazer agora."
                description="Conheça a profundidade técnica do V2 ou comece gratuitamente a organizar seu trabalho no GeoOffice Platform."
                centered
              />
            </Reveal>
            <div className="mx-auto mt-10 grid max-w-4xl gap-4 md:grid-cols-2">
              <ActionCard
                icon={Monitor}
                eyebrow="Quero produzir projetos"
                title="GeoOffice V2"
                description="Conheça o software completo e teste todos os módulos por 14 dias, sem restrição."
                href="/desktop"
                cta="Ir para o GeoOffice V2"
                product="v2"
              />
              <ActionCard
                icon={Cloud}
                eyebrow="Quero trabalhar online"
                title="GeoOffice Platform"
                description="Crie sua conta gratuita e conheça as aplicações técnicas em nuvem."
                href="/platform"
                cta="Ir para o GeoOffice Platform"
              />
            </div>
          </div>
        </section>
      </main>
    </MarketingShell>
  );
}

export function DesktopLanding() {
  return (
    <MarketingShell activeProduct="desktop">
      <main>
        <ProductHero
          eyebrow="GeoOffice V2 para Windows"
          title="Do levantamento ao projeto final, tudo em um só software."
          description="Processe dados de campo, modele terrenos, calcule volumes, desenvolva projetos e gere documentação técnica com ferramentas construídas para a realidade do profissional brasileiro."
          primary={{
            label: "Testar grátis por 14 dias",
            href: TRIAL_DESTINATION_PATH,
          }}
          secondary={{
            label: "Falar com um especialista",
            href: WHATSAPP_URL,
            external: true,
          }}
          image="/images/foto-trial7.jpg"
          imageAlt="Projetos viários no GeoOffice V2"
          icon={Monitor}
        />
        <V2ProofStrip />
        <section className="border-b border-border">
          <div className="mx-auto grid w-full max-w-7xl gap-12 px-4 py-20 md:px-8 md:py-24 lg:grid-cols-[0.8fr_1.2fr] lg:items-center lg:px-10">
            <Reveal>
              <SectionHeading
                eyebrow="Feito para entregar"
                title="Ferramentas que acompanham a complexidade do seu trabalho."
                description="Do primeiro dado de campo à documentação final, o V2 concentra operações técnicas em um ambiente profissional."
              />
            </Reveal>
            <Reveal delay={0.08} className="grid gap-3 sm:grid-cols-2">
              <Capability
                icon={Layers3}
                title="Módulo Georreferenciamento"
                description="A base essencial para topografia clássica e planialtimetria. Conta com automação de plantas e memoriais para loteamentos, além de georreferenciamento completo para SIGEF e INCRA."
                product="v2"
              />
              <Capability
                icon={MapPinned}
                title="Módulo Projetos"
                description="Solução completa para infraestrutura e obras em geral. Oferece ferramentas avançadas para cálculos de volumes, perfis, platôs com taludes automáticos e mapas de declividade."
                product="v2"
              />
              <Capability
                icon={Route}
                title="Módulo Estradas"
                description="Engenharia rodoviária de alto padrão para projetos pesados. Inclui recursos de superlargura, superelevação, Diagrama de Brückner, pavimentação e relatórios técnicos."
                product="v2"
              />
              <Capability
                icon={Sprout}
                title="Módulo Agricultura de Precisão"
                description="Máxima eficiência no campo com integração direta a pilotos automáticos. Otimiza o plantio e a colheita, maximizando a produção, reduzindo custos e aproveitando melhor o solo."
                product="v2"
              />
            </Reveal>
          </div>
        </section>
        <section id="modulos" className="scroll-mt-16 border-b border-border">
          <div className="mx-auto w-full max-w-7xl px-4 py-20 md:px-8 md:py-24 lg:px-10">
            <Reveal className="max-w-3xl">
              <SectionHeading
                eyebrow="Profissional / Licenças anuais"
                title="Quatro módulos. Cada um inclui tudo do anterior."
                description="Você começa pelo trabalho de hoje e amplia quando a operação exigir."
              />
            </Reveal>
            <div className="relative mt-10 grid gap-4 lg:grid-cols-2">
              {v2Modules.map((module, index) => (
                <Reveal key={module.name} delay={index * 0.05}>
                  <ModuleCard index={index + 1} {...module} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
        <section
          id="modalidades"
          className="scroll-mt-16 border-b border-border bg-muted/30"
        >
          <div className="mx-auto w-full max-w-7xl px-4 py-20 md:px-8 md:py-24 lg:px-10">
            <Reveal className="max-w-3xl">
              <SectionHeading
                eyebrow="Modalidades de acesso"
                title="Escolha como você quer começar."
                description="Trial e Educacional dão acesso ao V2 completo. A modalidade Profissional organiza a contratação por módulos cumulativos."
              />
            </Reveal>
            <div className="mt-10 grid gap-4 lg:grid-cols-3">
              <OfferCard
                icon={PlayCircle}
                title="Trial"
                badge="14 dias"
                description="Teste o GeoOffice V2 completo, com todos os módulos e ferramentas, antes de decidir."
                cta="Ativar trial"
                href={TRIAL_DESTINATION_PATH}
              />
              <OfferCard
                icon={GraduationCap}
                title="Educacional"
                badge="Universidades"
                description="Solicite uma licença para ensino e pesquisa por meio do formulário institucional."
                cta="Solicitar licença"
                href="#educacional"
                variant="outline"
              />
              <OfferCard
                icon={Building2}
                title="Profissional"
                badge="Anual"
                description="Escolha o nível de capacidade que sua operação precisa e avance pelos módulos cumulativos."
                cta="Ver módulos"
                href="#investimento"
                variant="outline"
              />
            </div>
          </div>
        </section>
        <section className="border-b border-border bg-muted/30">
          <div className="mx-auto w-full max-w-7xl px-4 py-20 md:px-8 md:py-24 lg:px-10">
            <Reveal className="max-w-3xl">
              <SectionHeading
                eyebrow="Por dentro do V2"
                title="Do desenho ao documento pronto para entregar."
                description="Explore uma jornada real de produção no GeoOffice V2, com resultados de diferentes módulos e etapas do trabalho."
              />
            </Reveal>
            <AutomaticGalleryCarousel />
          </div>
        </section>
        <V2DifferentialsSection />
        <V2FieldProofSection />
        <V2ContinuousLearningSection />
        <section className="border-b border-border">
          <div className="mx-auto grid w-full max-w-7xl gap-8 px-4 py-20 md:px-8 md:py-24 lg:grid-cols-[1fr_1fr] lg:items-center lg:px-10">
            <Reveal>
              <SectionHeading
                eyebrow="Do Windows para o navegador"
                title="Sua assinatura profissional também aproxima você da Platform."
                description="Os planos profissionais do V2 incluem benefícios equivalentes ao plano Pessoal do GeoOffice Platform. Organize arquivos, use aplicações online e conheça o outro universo com a mesma conta."
              />
              <Button className="mt-7" variant="outline" asChild>
                <Link href="/platform">
                  Conhecer o GeoOffice Platform
                  <ArrowRight />
                </Link>
              </Button>
            </Reveal>
            <Reveal delay={0.08}>
              <PlatformDashboardPreview />
            </Reveal>
          </div>
        </section>
        <V2TestimonialsSection />
        <V2ComparisonSection />
        <section
          id="educacional"
          className="scroll-mt-16 border-b border-border"
        >
          <div className="mx-auto grid w-full max-w-7xl gap-10 px-4 py-20 md:px-8 md:py-24 lg:grid-cols-[0.8fr_1.2fr] lg:items-start lg:px-10">
            <Reveal>
              <SectionHeading
                eyebrow="Para universidades e instituições"
                title="Leve o GeoOffice V2 para a sala de aula."
                description="A licença Educacional dá acesso ao produto completo para ensino e pesquisa, mediante análise da instituição. Envie os dados e nossa equipe entra em contato."
              />
            </Reveal>
            <Reveal delay={0.08}>
              <EducationalLeadForm />
            </Reveal>
          </div>
        </section>
        <V2ReadinessSection />
        <V2InvestmentSection />
        <ProductBottomCta product="v2" />
      </main>
    </MarketingShell>
  );
}

export function PlatformLanding({
  plans,
  plansLoadFailed = false,
}: {
  plans: AccountPlanModel[];
  plansLoadFailed?: boolean;
}) {
  const [billingCycle, setBillingCycle] = useState<PurchaseCycle>("MONTHLY");

  return (
    <MarketingShell activeProduct="platform">
      <main>
        <ProductHero
          eyebrow="GeoOffice Platform no navegador"
          title="Seu trabalho técnico, organizado e acessível onde você estiver."
          description="Centralize arquivos, trabalhe em áreas separadas, compartilhe com segurança e use aplicações online criadas para rotinas técnicas."
          primary={{
            label: "Criar conta grátis",
            href: "/auth/signup?product=platform",
          }}
          secondary={{ label: "Explorar recursos", href: "#recursos" }}
          image="/images/foto-trial18.jpg"
          imageAlt="Trabalho técnico organizado para acesso online"
          icon={Cloud}
          platform
        />
        <section id="recursos" className="scroll-mt-16 border-b border-border">
          <div className="mx-auto w-full max-w-7xl px-4 py-20 md:px-8 md:py-24 lg:px-10">
            <Reveal className="mx-auto max-w-3xl text-center">
              <SectionHeading
                eyebrow="Uma rotina conectada"
                title="Do arquivo ao resultado, sem perder o contexto."
                description="A Platform organiza o trabalho em uma sequência simples: centralizar, compartilhar e resolver tarefas técnicas no navegador."
                centered
              />
            </Reveal>
            <div className="mt-10 grid gap-4 lg:grid-cols-3">
              {platformJourneys.map((journey, index) => (
                <Reveal key={journey.step} delay={index * 0.05}>
                  <PlatformJourney {...journey} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
        <section className="border-b border-border bg-muted/30">
          <div className="mx-auto w-full max-w-7xl px-4 py-20 md:px-8 md:py-24 lg:px-10">
            <Reveal className="max-w-3xl">
              <SectionHeading
                eyebrow="Aplicações online"
                title="Recursos técnicos que resolvem tarefas específicas."
                description="Comece pelo que você precisa hoje e mantenha o restante do trabalho organizado no mesmo ambiente."
              />
            </Reveal>
            <Reveal delay={0.08}>
              <PlatformToolsBento />
            </Reveal>
          </div>
        </section>
        <Viewer3DShowcase />
        <section className="border-b border-border">
          <div className="mx-auto grid w-full max-w-7xl gap-10 px-4 py-20 md:px-8 md:py-24 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:px-10">
            <Reveal>
              <PlatformDashboardPreview />
            </Reveal>
            <Reveal delay={0.08}>
              <SectionHeading
                eyebrow="Tudo no mesmo ambiente"
                title="Arquivos, pessoas e aplicações trabalham juntos."
                description="Use workspaces para separar contextos, mantenha os arquivos próximos de quem precisa deles e abra aplicações online a partir da mesma conta."
              />
              <div className="mt-7 space-y-4">
                <WorkflowPoint
                  icon={FolderOpen}
                  title="Organize por contexto"
                  description="Projetos, clientes e equipes ficam separados sem fragmentar sua conta."
                />
                <WorkflowPoint
                  icon={Share2}
                  title="Compartilhe com intenção"
                  description="Convide participantes e distribua informações no workspace adequado."
                />
                <WorkflowPoint
                  icon={FileText}
                  title="Produza e preserve resultados"
                  description="As aplicações resolvem tarefas específicas dentro da rotina online."
                />
              </div>
            </Reveal>
          </div>
        </section>
        <section className="border-b border-border">
          <div className="mx-auto w-full max-w-7xl px-4 py-20 md:px-8 md:py-24 lg:px-10">
            <Reveal className="max-w-3xl">
              <SectionHeading
                eyebrow="Planos do GeoOffice Platform"
                title="Comece grátis. Amplie quando sua rotina pedir."
                description="O Pessoal Free é independente do GeoOffice V2. Assinantes profissionais do V2 recebem benefícios equivalentes ao Pessoal."
              />
            </Reveal>
            <div className="mt-6 inline-flex rounded-md border border-border bg-muted p-1">
              <Button
                size="sm"
                variant={billingCycle === "MONTHLY" ? "default" : "ghost"}
                onClick={() => setBillingCycle("MONTHLY")}
              >
                Mensal
              </Button>
              <Button
                size="sm"
                variant={billingCycle === "YEARLY" ? "default" : "ghost"}
                onClick={() => setBillingCycle("YEARLY")}
              >
                Anual
              </Button>
            </div>
            {plansLoadFailed ? (
              <Alert variant="destructive" className="mt-10">
                <AlertTitle>Não foi possível carregar os planos</AlertTitle>
                <AlertDescription>
                  Tente novamente em alguns instantes.
                </AlertDescription>
              </Alert>
            ) : plans.length === 0 ? (
              <p className="mt-10 text-sm text-muted-foreground">
                Nenhum plano está disponível no momento.
              </p>
            ) : (
              <div className="mt-10 grid items-stretch gap-4 lg:grid-cols-3">
                {plans.map((plan, index) => (
                  <Reveal
                    key={plan.id ?? `${plan.planType}-${plan.name}`}
                    className="h-full"
                    delay={index * 0.05}
                  >
                    <PlatformPlanCard
                      plan={plan}
                      featured={plan.planType === "PERSONAL"}
                      billingCycle={billingCycle}
                    />
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </section>
        <section className="border-b border-border">
          <div className="mx-auto grid w-full max-w-7xl gap-10 px-4 py-20 md:px-8 md:py-24 lg:grid-cols-2 lg:items-center lg:px-10">
            <Reveal>
              <SectionHeading
                eyebrow="Produto independente, conta compartilhada"
                title="Comece na Platform sem precisar do V2."
                description="O plano Pessoal Free é uma porta de entrada própria para arquivos e aplicações online. Se você também usa um plano profissional do V2, a mesma conta reúne os dois universos e inclui benefícios equivalentes ao Pessoal."
              />
            </Reveal>
            <Reveal delay={0.08}>
              <div className="grid gap-4 sm:grid-cols-2">
                <RelationshipPanel
                  icon={Cloud}
                  title="Somente Platform"
                  description="Crie sua conta Pessoal Free e use o produto Web de forma independente."
                />
                <RelationshipPanel
                  icon={Monitor}
                  title="Platform com V2"
                  description="Gerencie os dois produtos com a mesma conta e acesse o benefício incluído."
                  product="v2"
                />
              </div>
            </Reveal>
          </div>
        </section>
        <section className="border-b border-border bg-muted/30">
          <div className="mx-auto grid w-full max-w-7xl gap-10 px-4 py-20 md:px-8 md:py-24 lg:grid-cols-[0.8fr_1.2fr] lg:items-start lg:px-10">
            <Reveal>
              <SectionHeading
                eyebrow="Em evolução"
                title="O GeoOffice Platform continua crescendo com o seu trabalho."
                description="Algumas capacidades estão em construção e serão comunicadas quando estiverem prontas para uso."
              />
            </Reveal>
            <Reveal delay={0.08}>
              <div className="divide-y divide-border border-y border-border">
                <RoadmapItem
                  icon={HardDrive}
                  title="Backup automatizado"
                  description="Mais segurança para os arquivos armazenados na nuvem."
                />
                <RoadmapItem
                  icon={Database}
                  title="Mais armazenamento"
                  description="Opções adicionais, como 100 GB e 1 TB, para diferentes necessidades."
                />
                <RoadmapItem
                  icon={Users}
                  title="Equipes e assentos"
                  description="Uma experiência de colaboração preparada para operações maiores."
                />
                <RoadmapItem
                  icon={Workflow}
                  title="Novas conexões"
                  description="Conexões cada vez mais fluidas entre o V2 e o GeoOffice Platform."
                />
              </div>
            </Reveal>
          </div>
        </section>
        <ProductBottomCta product="platform" />
      </main>
    </MarketingShell>
  );
}

function V2ProofStrip() {
  return (
    <section
      className="border-b border-product-v2 bg-product-v2 text-product-v2-foreground"
      aria-labelledby="v2-proof-title"
    >
      <h2 id="v2-proof-title" className="sr-only">
        GeoOffice V2 em números
      </h2>
      <div className="mx-auto grid w-full max-w-7xl grid-cols-2 px-4 py-8 md:px-8 lg:grid-cols-4 lg:px-10">
        {v2ProofStats.map((stat, index) => (
          <Reveal key={stat.value} delay={index * 0.04}>
            <div
              className={cn(
                "h-full px-4 py-3 sm:px-6",
                index % 2 !== 0 && "border-l border-product-v2-foreground/25",
                index > 1 &&
                  "border-t border-product-v2-foreground/25 lg:border-t-0",
                index > 0 && "lg:border-l",
              )}
            >
              <p className="font-mono text-2xl font-semibold tracking-tight tabular-nums md:text-3xl">
                {stat.value}
              </p>
              <p className="mt-2 max-w-48 text-xs leading-5 text-product-v2-foreground/80 sm:text-sm">
                {stat.label}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function AutomaticGalleryCarousel() {
  const autoplay = useRef(
    Autoplay({ delay: 4500, stopOnInteraction: false, stopOnMouseEnter: true }),
  );
  const [api, setApi] = useState<CarouselApi>();
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    if (!api) return;

    const updateScrollProgress = () => {
      setScrollProgress(api.scrollProgress());
    };

    updateScrollProgress();
    api.on("reInit", updateScrollProgress);
    api.on("scroll", updateScrollProgress);
    api.on("select", updateScrollProgress);

    return () => {
      api.off("reInit", updateScrollProgress);
      api.off("scroll", updateScrollProgress);
      api.off("select", updateScrollProgress);
    };
  }, [api]);

  const snapPoints =
    api?.scrollSnapList() ??
    v2Gallery.map((_, index) => index / Math.max(v2Gallery.length - 1, 1));
  const snapSpacing =
    snapPoints.length > 1
      ? Math.min(
          ...snapPoints.flatMap((point, index) => {
            const previousPoint = snapPoints[index - 1];

            return previousPoint === undefined ? [] : [point - previousPoint];
          }),
        )
      : 1;

  return (
    <Carousel
      className="mx-auto mt-10 max-w-4xl"
      aria-label="Galeria de resultados do GeoOffice V2"
      opts={{ align: "center", duration: 42, loop: true }}
      plugins={[autoplay.current]}
      setApi={setApi}
    >
      <CarouselContent className="-ml-4 items-end" viewportClassName="py-5">
        {v2Gallery.map(([title, description, image], index) => (
          <AnimatedGallerySlide
            key={title}
            snapPoint={snapPoints[index] ?? 0}
            snapSpacing={snapSpacing}
            scrollProgress={scrollProgress}
          >
            <div className="overflow-hidden rounded-lg border border-border bg-card">
              <div className="relative aspect-[1280/685] bg-muted">
                <Image
                  src={image}
                  alt={title}
                  fill
                  sizes="(max-width: 1024px) 100vw, 896px"
                  className="object-contain"
                />
              </div>
              <div className="p-6 md:p-8">
                <Badge variant="outline" className="w-fit">
                  Fluxo de produção
                </Badge>
                <h3 className="cn-font-heading mt-5 text-2xl font-semibold tracking-[-0.03em]">
                  {title}
                </h3>
                <p className="mt-3 text-sm leading-6 text-muted-foreground">
                  {description}
                </p>
              </div>
            </div>
          </AnimatedGallerySlide>
        ))}
      </CarouselContent>
      <Button
        type="button"
        size="icon"
        variant="outline"
        className="absolute top-1/2 left-2 z-20 -translate-y-1/2 cursor-pointer sm:-left-12"
        aria-label="Ver imagem anterior"
        onClick={() => api?.scrollPrev()}
      >
        <ChevronLeft />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="outline"
        className="absolute top-1/2 right-2 z-20 -translate-y-1/2 cursor-pointer sm:-right-12"
        aria-label="Ver próxima imagem"
        onClick={() => api?.scrollNext()}
      >
        <ChevronRight />
      </Button>
    </Carousel>
  );
}

function AnimatedGallerySlide({
  snapPoint,
  snapSpacing,
  scrollProgress,
  children,
}: {
  snapPoint: number;
  snapSpacing: number;
  scrollProgress: number;
  children: ReactNode;
}) {
  const distance = Math.abs(scrollProgress - snapPoint);
  const loopedDistance = Math.min(distance, 1 - distance);
  const emphasis = Math.max(0, 1 - loopedDistance / snapSpacing);
  const scale = 0.94 + emphasis * 0.09;
  const translateY = -12 * emphasis;

  return (
    <CarouselItem
      className="basis-[84%] origin-bottom pl-4 will-change-transform sm:basis-[78%] lg:basis-[72%]"
      style={{
        opacity: 0.7 + emphasis * 0.3,
        transform: `translateY(${translateY}px) scale(${scale})`,
      }}
    >
      {children}
    </CarouselItem>
  );
}

function V2DifferentialsSection() {
  const cards = [
    [
      "CAD Proprietário",
      "Uma plataforma CAD brasileira, própria",
      "Única plataforma CAD proprietária do setor no Brasil, com comandos similares ao AutoCAD — reduz a curva de aprendizado de quem já usa outro CAD.",
    ],
    [
      "Licenciamento modular",
      "Você assina só o que precisa",
      "Único software do setor vendido por módulo — sem pacote fechado te obrigando a pagar por ferramentas que você não vai usar.",
    ],
    [
      "Estradas e Agricultura",
      "Cobertura que a concorrência não tem",
      "Únicos no mercado brasileiro com módulos dedicados de Estradas e Agricultura de Precisão dentro do mesmo software.",
    ],
    [
      "100% Offline",
      "Feito para funcionar sem sinal",
      "Funciona sem internet em qualquer tipo de licença — pensado para campo e obra, não só para escritório com boa conexão.",
    ],
  ] as const;

  return (
    <MarketingCardSection
      eyebrow="Por que o GeoOffice V2"
      title="Diferenciais que se sentem no dia a dia de campo."
      cards={cards}
    />
  );
}

function V2FieldProofSection() {
  const cards = [
    [
      "Aplicações",
      "Do rural à obra de estrada",
      "Agrimensores autônomos, empresas de topografia, mineradoras, construtoras e órgãos públicos usam o GeoOffice V2 em georreferenciamento rural e urbano, terraplenagem, volumetria e projetos de estradas.",
    ],
    [
      "Formação técnica",
      "Presente em 46 instituições de ensino",
      "Universidades, escolas técnicas e institutos federais usam o GeoOffice em laboratório — mais de 1.700 licenças educacionais formando profissionais em todo o Brasil.",
    ],
    [
      "Conformidade",
      "100% aderente às normas do setor",
      "Layers NTGIR, relatórios SIGEF e conformidade com a Lei 10.267/01 (INCRA), além de padrões ABNT — sem retrabalho por exigência normativa.",
    ],
  ] as const;

  return (
    <MarketingCardSection
      eyebrow="Prova em campo"
      title="Usado em obra, em escritório e em sala de aula."
      cards={cards}
      muted
    />
  );
}

function MarketingCardSection({
  eyebrow,
  title,
  cards,
  muted = false,
}: {
  eyebrow: string;
  title: string;
  cards: readonly (readonly [string, string, string])[];
  muted?: boolean;
}) {
  return (
    <section className={cn("border-b border-border", muted && "bg-muted/30")}>
      <div className="mx-auto w-full max-w-7xl px-4 py-20 md:px-8 md:py-24 lg:px-10">
        <Reveal className="max-w-3xl">
          <SectionHeading eyebrow={eyebrow} title={title} />
        </Reveal>
        <div
          className={cn(
            "mt-10 grid gap-4",
            cards.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4",
          )}
        >
          {cards.map(([label, heading, description], index) => (
            <Reveal key={label} delay={index * 0.05}>
              <Card className="h-full py-6">
                <CardHeader>
                  <p className="font-mono text-xs font-semibold uppercase tracking-[0.16em] text-product-v2">
                    {label}
                  </p>
                  <CardTitle className="mt-4 text-lg">{heading}</CardTitle>
                  <CardDescription className="leading-6">
                    {description}
                  </CardDescription>
                </CardHeader>
              </Card>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function V2ContinuousLearningSection() {
  return (
    <section className="border-b border-border">
      <div className="mx-auto grid w-full max-w-7xl gap-10 px-4 py-20 md:px-8 md:py-24 lg:grid-cols-[0.8fr_1.2fr] lg:items-center lg:px-10">
        <Reveal>
          <SectionHeading
            eyebrow="Aprendizado contínuo"
            title="Mais de 300 vídeo-aulas pra aprender no seu ritmo."
            description="Aulas práticas e aplicadas, direto do canal oficial no YouTube — do primeiro cálculo de poligonal ao projeto de estrada completo."
          />
          <Button className="mt-7" variant="outline" asChild>
            <a href={YOUTUBE_URL}>
              Ver canal no YouTube <ExternalLink />
            </a>
          </Button>
        </Reveal>
        <Reveal delay={0.12} className="lg:col-start-2 lg:row-start-1">
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <div className="relative aspect-video bg-muted">
              <iframe
                src="https://www.youtube.com/embed/3kF3Q1Kl6l0"
                title="Vídeo institucional do GeoOffice V2"
                className="absolute inset-0 size-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function V2ComparisonSection() {
  const common = [
    "Licença limitada",
    "Vinculada a um único computador, por código de ativação",
    "Alguns exigem internet para funcionar",
    "Cliente fica preso ao escritório",
    "Pacote fechado — cliente não escolhe o que precisa",
    "Plugin LISP não é software. Aplicação incompleta e depende de programa CAD, o que abre portas para a pirataria.",
    "Softwares antigos, sem atualização e sem suporte",
  ];
  const v2 = [
    "Licença flutuante",
    "Pode ser transferida para outro computador sempre que você precisar",
    "Não fica presa a uma única máquina",
    "Continua funcionando 100% offline",
    "Liberdade de uso — garante seu trabalho de qualquer lugar",
    "Módulos específicos para sua demanda — economia na aquisição",
    "Acesso incluso ao GeoOffice Platform, com ferramentas online, espaço e gestão de dados",
    "Atualizações periódicas",
    "Suporte técnico de alto nível incluso",
  ];
  return (
    <section className="border-b border-border bg-muted/30">
      <div className="mx-auto w-full max-w-7xl px-4 py-20 md:px-8 md:py-24 lg:px-10">
        <Reveal className="max-w-3xl">
          <SectionHeading
            eyebrow="Comparado à concorrência"
            title="O que muda quando você troca para o GeoOffice V2."
            description="Boa parte do mercado ainda trava o profissional com licença de código de ativação, vinculada a um único computador — parecido com o modelo de ativação do Windows. O GeoOffice V2 usa licença flutuante: você transfere entre máquinas sempre que precisar."
          />
        </Reveal>
        <div className="mt-10 grid gap-4 lg:grid-cols-2">
          <ComparisonCard
            title="Comum na concorrência"
            heading="Trava o profissional"
            items={common}
          />
          <ComparisonCard
            title="GeoOffice V2"
            heading="Liberdade com solidez técnica"
            items={v2}
            positive
          />
        </div>
      </div>
    </section>
  );
}

function ComparisonCard({
  title,
  heading,
  items,
  positive = false,
}: {
  title: string;
  heading: string;
  items: readonly string[];
  positive?: boolean;
}) {
  const Icon = positive ? CircleCheck : CircleX;
  return (
    <Card className={cn("h-full py-6", positive && "product-v2-card")}>
      <CardHeader>
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          {title}
        </p>
        <CardTitle className="mt-4 text-xl">{heading}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-3">
          {items.map((item) => (
            <li
              key={item}
              className="flex items-start gap-2.5 text-sm leading-6"
            >
              <Icon
                className={cn(
                  "mt-1 size-4 shrink-0",
                  positive ? "text-product-v2" : "text-muted-foreground",
                )}
              />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function V2ReadinessSection() {
  return (
    <section className="border-b border-border">
      <div className="mx-auto grid w-full max-w-7xl gap-10 px-4 py-20 md:px-8 md:py-24 lg:grid-cols-[0.75fr_1.25fr] lg:items-start lg:px-10">
        <Reveal>
          <SectionHeading
            eyebrow="Compatibilidade e apoio"
            title="Preparado para entrar na sua rotina técnica."
            description="Antes, durante e depois da instalação, a conta GeoOffice reúne os caminhos para ativar, aprender e pedir ajuda."
          />
        </Reveal>
        <div className="grid gap-4 sm:grid-cols-2">
          <Capability
            icon={Cpu}
            title="Windows 10 e 11"
            description="Software instalável para o ambiente Windows, com requisitos detalhados antes da ativação."
            product="v2"
          />
          <Capability
            icon={BookOpen}
            title="Treinamento contínuo"
            description="Videoaulas e materiais ajudam a avançar do primeiro cálculo aos projetos mais completos."
            product="v2"
          />
          <Capability
            icon={ShieldCheck}
            title="Licenças e downloads"
            description="A conta GeoOffice centraliza trial, licenças, instaladores e atualizações do V2."
            product="v2"
          />
          <Capability
            icon={MessageCircle}
            title="Central de suporte"
            description="Acesse a central de suporte e os canais de atendimento quando precisar."
            product="v2"
          />
        </div>
      </div>
    </section>
  );
}

function V2InvestmentSection() {
  const plans = [
    [
      "Georreferenciamento",
      "R$ 999,90",
      "Módulo de entrada — base do GeoOffice V2.",
    ],
    ["Projetos", "R$ 1.679,00", "+ inclui Georreferenciamento"],
    ["Estradas", "R$ 2.800,00", "+ inclui módulos anteriores"],
    ["Agricultura de Precisão", "R$ 6.500,00", "+ inclui todos os módulos"],
  ] as const;
  return (
    <section
      id="investimento"
      className="scroll-mt-16 border-b border-border bg-muted/30"
    >
      <div className="mx-auto w-full max-w-7xl px-4 py-20 md:px-8 md:py-24 lg:px-10">
        <Reveal className="max-w-3xl">
          <SectionHeading
            eyebrow="Investimento"
            title="Comece pelo módulo que resolve o seu trabalho hoje."
            description="Os módulos são cumulativos: ao assinar um módulo superior, você já tem tudo dos anteriores incluído."
          />
        </Reveal>
        <Reveal delay={0.04}>
          <Alert className="mt-8 border-warning bg-warning/10">
            <AlertTriangle />
            <AlertTitle>
              Pagamento direto pela página está em construção
            </AlertTitle>
            <AlertDescription>
              Por enquanto, o botão “Assinar” leva ao contato com o time de
              vendas.
            </AlertDescription>
          </Alert>
        </Reveal>
        <div className="mt-10 grid gap-4 lg:grid-cols-4">
          {plans.map(([name, price, description], index) => (
            <Reveal key={name} delay={index * 0.05}>
              <Card className="h-full py-6">
                <CardHeader>
                  <CardTitle className="text-lg">{name}</CardTitle>
                  <p className="mt-4 font-mono text-2xl font-semibold tabular-nums">
                    {price}
                  </p>
                  <CardDescription>por ano</CardDescription>
                  <p className="pt-3 text-sm leading-6 text-muted-foreground">
                    {description}
                  </p>
                </CardHeader>
                <CardFooter className="mt-auto">
                  <Button
                    className="product-v2-action product-v2-gradient w-full cursor-pointer text-product-v2-foreground"
                    asChild
                  >
                    <a href={WHATSAPP_URL}>
                      Assinar <ArrowRight />
                    </a>
                  </Button>
                </CardFooter>
              </Card>
            </Reveal>
          ))}
        </div>
        <div className="mt-6 grid gap-3 text-sm text-muted-foreground md:grid-cols-3">
          <p>Licença flutuante, por assinatura de 12 meses</p>
          <p>
            Parcelamento em até 12x sem juros no cartão, para qualquer módulo
          </p>
          <p>Desconto disponível em renovação anual</p>
        </div>
      </div>
    </section>
  );
}

function V2TestimonialsSection() {
  return (
    <section
      className="border-b border-border bg-muted/30"
      aria-labelledby="v2-testimonials-title"
    >
      <div className="mx-auto w-full max-w-7xl px-4 py-20 md:px-8 md:py-24 lg:px-10">
        <Reveal className="max-w-3xl">
          <div>
            <p className="mb-3 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-foreground/80">
              Quem usa, recomenda
            </p>
            <h2
              id="v2-testimonials-title"
              className="cn-font-heading text-3xl font-semibold tracking-[-0.035em] md:text-4xl"
            >
              Depoimentos de quem está no campo.
            </h2>
          </div>
        </Reveal>
        <div className="mt-10 grid gap-4 lg:grid-cols-3">
          {v2Testimonials.map((testimonial, index) => (
            <Reveal key={testimonial.name} delay={index * 0.06}>
              <Card className="h-full py-6">
                <CardContent className="flex h-full flex-col">
                  <Quote
                    className="size-7 text-product-v2"
                    aria-hidden="true"
                  />
                  <blockquote className="mt-5 text-base leading-7 text-foreground">
                    “{testimonial.quote}”
                  </blockquote>
                  <div className="mt-8 flex items-center gap-3 border-t border-border pt-5">
                    <Avatar size="lg">
                      {testimonial.image ? (
                        <AvatarImage
                          src={testimonial.image}
                          alt={`Foto de ${testimonial.name}`}
                        />
                      ) : null}
                      <AvatarFallback className="bg-product-v2-subtle font-mono font-semibold text-product-v2">
                        {testimonial.initials}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-semibold">
                        {testimonial.name}
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {testimonial.role}, {testimonial.location}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductHero({
  eyebrow,
  title,
  description,
  primary,
  secondary,
  image,
  imageAlt,
  icon: Icon,
  platform = false,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  primary: { label: string; href: string };
  secondary: { label: string; href: string; external?: boolean };
  image: string;
  imageAlt: string;
  icon: LucideIcon;
  platform?: boolean;
}) {
  return (
    <section className="relative isolate overflow-hidden border-b border-border">
      <TopographicBackdrop />
      <div className="mx-auto grid w-full max-w-7xl gap-12 px-4 py-20 md:px-8 md:py-28 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:px-10">
        <Reveal>
          <Badge variant="outline" className="gap-2">
            <Icon
              className={cn(
                "size-3.5",
                platform ? "text-foreground" : "text-product-v2",
              )}
            />
            {eyebrow}
          </Badge>
          <h1 className="cn-font-heading mt-7 max-w-3xl text-4xl font-semibold leading-[1.02] tracking-[-0.05em] md:text-6xl">
            {title}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-7 text-muted-foreground md:text-lg">
            {description}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button
              size="lg"
              className={cn(
                MARKETING_CTA_CLASS,
                "w-full cursor-pointer sm:w-auto",
                !platform &&
                  "product-v2-action product-v2-gradient text-product-v2-foreground",
              )}
              asChild
            >
              <Link href={primary.href}>
                {primary.label}
                <ArrowRight />
              </Link>
            </Button>
            {secondary.external ? (
              <Button
                size="lg"
                variant="outline"
                className={cn(
                  MARKETING_CTA_CLASS,
                  "w-full cursor-pointer sm:w-auto",
                )}
                asChild
              >
                <a href={secondary.href}>
                  <MessageCircle />
                  {secondary.label}
                </a>
              </Button>
            ) : (
              <Button
                size="lg"
                variant="outline"
                className={cn(
                  MARKETING_CTA_CLASS,
                  "w-full cursor-pointer sm:w-auto",
                )}
                asChild
              >
                <Link href={secondary.href}>{secondary.label}</Link>
              </Button>
            )}
          </div>
          <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Check
                className={cn(
                  "size-3.5",
                  platform ? "text-foreground" : "text-product-v2",
                )}
              />
              {platform ? "Acesso pelo navegador" : "Windows 10 e 11"}
            </span>
            <span className="flex items-center gap-1.5">
              <Check
                className={cn(
                  "size-3.5",
                  platform ? "text-foreground" : "text-product-v2",
                )}
              />
              {platform
                ? "Plano Pessoal Free disponível"
                : "Trial completo por 14 dias"}
            </span>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="overflow-hidden rounded-lg border border-border bg-card shadow-xl shadow-primary/5">
            <div className="flex h-11 items-center justify-between border-b border-border px-4">
              <div className="flex items-center gap-2">
                <span className="size-2 rounded-full bg-success" />
                <span className="font-mono text-[0.625rem] uppercase tracking-[0.14em] text-muted-foreground">
                  {platform
                    ? "GeoOffice Platform / Online"
                    : "GeoOffice V2 / Windows"}
                </span>
              </div>
              <Badge variant="outline">Produto real</Badge>
            </div>
            <div className="relative aspect-[1.55] overflow-hidden bg-muted">
              {platform ? (
                <PlatformDashboardPreview compact />
              ) : (
                <>
                  <Image
                    src={image}
                    alt={imageAlt}
                    fill
                    sizes="(max-width: 1024px) 100vw, 55vw"
                    className="object-cover object-top"
                  />
                  <div className="absolute inset-x-0 bottom-0 border-t border-border bg-background/90 px-4 py-3 backdrop-blur">
                    <span className="text-xs font-medium">
                      Produção técnica
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/**
 * Precisa casar com uma chave de `EDUCATIONAL_CONSENT_TEXTS` no backend
 * (`libs/modules/crm/domain/educational-consent.ts`), que e quem resolve a
 * versao para o texto gravado.
 *
 * O texto abaixo esta duplicado aqui so para ser exibido. Isso e seguro porque
 * versoes sao imutaveis: se o texto mudar, cria-se uma versao nova. Enviar uma
 * versao antiga faz o backend gravar o texto antigo — exatamente o que foi
 * mostrado. O unico jeito de divergir seria reescrever o texto de uma versao
 * existente, que o registro do backend proibe.
 */
const EDUCATIONAL_CONSENT_VERSION = "educational-2026-09";
const EDUCATIONAL_CONSENT_TEXT =
  "Autorizo o contato da equipe GeoOffice pelos dados informados e o " +
  "tratamento deles para análise da solicitação de licença Educacional. " +
  "Posso solicitar a exclusão dos meus dados a qualquer momento pelos " +
  "canais de atendimento.";

function EducationalLeadForm() {
  const [status, setStatus] = useState<
    "idle" | "sending" | "success" | "error" | "throttled"
  >("idle");
  const [reference, setReference] = useState<string | null>(null);

  /**
   * Gerado uma vez por formulario montado e reenviado em cada tentativa: e o
   * que torna o retry seguro. Duplo clique ou rede instavel repetem a mesma
   * chave, e o backend devolve a submissao original em vez de criar uma
   * segunda oportunidade.
   *
   * Regenerado apos sucesso, porque ai comeca uma submissao nova de verdade.
   */
  const [submissionId, setSubmissionId] = useState(() =>
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : "",
  );

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("sending");
    const form = event.currentTarget;
    const raw = Object.fromEntries(new FormData(form).entries());

    const estimatedUsers = String(raw.estimatedUsers ?? "").trim();

    try {
      const response = await fetch("/api/educational-leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          submissionId,
          contactName: raw.contactName,
          email: raw.email,
          phone: raw.phone,
          institutionName: raw.institutionName,
          need: raw.need,
          role: String(raw.role ?? "").trim() || undefined,
          estimatedUsers: estimatedUsers ? Number(estimatedUsers) : undefined,
          consentVersion: EDUCATIONAL_CONSENT_VERSION,
          consentAccepted: raw.consentAccepted === "on",
          website: raw.website ?? "",
        }),
      });

      if (response.status === 429) {
        setStatus("throttled");
        return;
      }
      if (!response.ok) throw new Error("lead submission failed");

      const payload = (await response.json()) as { reference?: string | null };
      setReference(payload.reference ?? null);
      form.reset();
      setSubmissionId(crypto.randomUUID());
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="product-v2-gradient flex size-10 shrink-0 items-center justify-center rounded-md border">
            <GraduationCap className="size-5" />
          </div>
          <div>
            <CardTitle>Solicitar licença Educacional</CardTitle>
            <CardDescription className="mt-1">
              Envie os dados da instituição para nossa equipe.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={handleSubmit}>
          {/*
            Honeypot. A rota ja rejeitava a submissao quando `website` vinha
            preenchido, mas o campo nunca existia no HTML, entao a verificacao
            nunca capturava nada. Fica fora da viewport em vez de
            `display:none`, que parte dos bots reconhece e evita, e fora da
            ordem de tabulacao para nao alcancar quem usa teclado ou leitor.
          */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            aria-hidden="true"
            autoComplete="off"
            className="absolute -left-[9999px] size-px overflow-hidden"
          />
          <div className="space-y-2">
            <label htmlFor="education-contact" className="text-sm font-medium">
              Responsável
            </label>
            <Input
              id="education-contact"
              name="contactName"
              required
              placeholder="Nome completo"
            />
          </div>
          <div className="space-y-2">
            <label
              htmlFor="education-institution"
              className="text-sm font-medium"
            >
              Instituição
            </label>
            <Input
              id="education-institution"
              name="institutionName"
              required
              placeholder="Nome da universidade"
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="education-email" className="text-sm font-medium">
              E-mail institucional
            </label>
            <Input
              id="education-email"
              name="email"
              type="email"
              required
              placeholder="voce@universidade.edu.br"
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="education-phone" className="text-sm font-medium">
              Telefone ou WhatsApp
            </label>
            <Input
              id="education-phone"
              name="phone"
              required
              placeholder="(00) 00000-0000"
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="education-role" className="text-sm font-medium">
              Sua função{" "}
              <span className="text-muted-foreground">(opcional)</span>
            </label>
            <Input
              id="education-role"
              name="role"
              placeholder="Professor, coordenador..."
            />
          </div>
          <div className="space-y-2">
            {/* Rotulo curto de proposito: "Quantidade estimada de usuarios"
                quebrava em duas linhas nesta coluna e desalinhava este campo
                do vizinho da esquerda. */}
            <label htmlFor="education-users" className="text-sm font-medium">
              Nº de usuários{" "}
              <span className="text-muted-foreground">(opcional)</span>
            </label>
            <Input
              id="education-users"
              name="estimatedUsers"
              type="number"
              min={1}
              inputMode="numeric"
              placeholder="40"
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <label htmlFor="education-need" className="text-sm font-medium">
              Como pretende utilizar o GeoOffice?
            </label>
            <Input
              id="education-need"
              name="need"
              required
              placeholder="Ensino, pesquisa, laboratório..."
            />
          </div>
          <div className="sm:col-span-2">
            <label
              htmlFor="education-consent"
              className="flex cursor-pointer items-start gap-3 text-sm text-muted-foreground"
            >
              <input
                id="education-consent"
                name="consentAccepted"
                type="checkbox"
                required
                className="mt-0.5 size-4 shrink-0 cursor-pointer accent-current"
              />
              <span>{EDUCATIONAL_CONSENT_TEXT}</span>
            </label>
          </div>
          <div className="sm:col-span-2">
            <Button
              type="submit"
              size="lg"
              className={cn(
                MARKETING_CTA_CLASS,
                "product-v2-action product-v2-gradient w-full cursor-pointer text-product-v2-foreground",
              )}
              disabled={status === "sending"}
            >
              {status === "sending"
                ? "Enviando solicitação..."
                : "Enviar solicitação"}
              <ArrowRight />
            </Button>
          </div>
          {status === "success" ? (
            <p className="sm:col-span-2 text-sm text-success" role="status">
              Solicitação enviada. Nossa equipe entrará em contato.
              {reference ? (
                <>
                  {" "}
                  Guarde a referência <strong>{reference}</strong>.
                </>
              ) : null}
            </p>
          ) : null}
          {status === "throttled" ? (
            <p className="sm:col-span-2 text-sm text-warning" role="alert">
              Muitas solicitações em pouco tempo. Aguarde alguns minutos e
              tente novamente.
            </p>
          ) : null}
          {status === "error" ? (
            <p className="sm:col-span-2 text-sm text-destructive" role="alert">
              Não foi possível enviar agora. Tente novamente em instantes.
            </p>
          ) : null}
        </form>
      </CardContent>
    </Card>
  );
}

function ProductPortal({
  icon: Icon,
  eyebrow,
  title,
  description,
  features,
  href,
  cta,
  image,
  imageAlt,
  product,
}: {
  icon: LucideIcon;
  eyebrow: string;
  title: string;
  description: string;
  features: readonly string[];
  href: string;
  cta: string;
  image: string;
  imageAlt: string;
  product: "v2" | "platform";
}) {
  const isPlatform = product === "platform";

  return (
    <Reveal>
      <Card
        className={cn(
          "h-full overflow-hidden py-0 transition-transform duration-300 hover:-translate-y-1",
          isPlatform
            ? "border-primary/60 border-t-2 border-t-primary bg-card ring-1 ring-primary/30"
            : "product-v2-card",
        )}
      >
        {isPlatform ? (
          <PlatformDashboardPreview />
        ) : (
          <div className="relative aspect-[2.1] overflow-hidden bg-muted">
            <Image
              src={image}
              alt={imageAlt}
              fill
              sizes="(max-width: 1024px) 100vw, 50vw"
              className="object-cover object-top"
            />
            <div className="absolute inset-0 bg-product-v2-subtle" />
          </div>
        )}
        <CardHeader className="p-6">
          <div className="flex items-center justify-between gap-3">
            <div
              className={cn(
                "flex size-10 items-center justify-center rounded-md border",
                isPlatform
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-product-v2 bg-product-v2 text-product-v2-foreground",
              )}
            >
              <Icon className="size-5" />
            </div>
            <Badge
              variant="default"
              className={
                isPlatform
                  ? "bg-primary text-primary-foreground"
                  : "product-v2-gradient"
              }
            >
              {eyebrow}
            </Badge>
          </div>
          <CardTitle className="mt-5 text-2xl">{title}</CardTitle>
          <CardDescription className="max-w-xl text-sm leading-6">
            {description}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-6 pb-6">
          <div className="flex flex-wrap gap-2">
            {features.map((feature) => (
              <Badge
                key={feature}
                variant="outline"
                className={
                  isPlatform
                    ? "border-primary/40 text-foreground"
                    : "border-product-v2/50 bg-product-v2-subtle text-foreground"
                }
              >
                {feature}
              </Badge>
            ))}
          </div>
        </CardContent>
        <CardFooter className="mt-auto px-6 pb-6">
          <Button
            size="lg"
            className={cn(
              MARKETING_CTA_CLASS,
              "w-full cursor-pointer",
              !isPlatform &&
                "product-v2-action product-v2-gradient text-product-v2-foreground",
            )}
            asChild
          >
            <Link href={href}>
              {cta}
              <ArrowRight />
            </Link>
          </Button>
        </CardFooter>
      </Card>
    </Reveal>
  );
}

function PlatformDashboardPreview({ compact = false }: { compact?: boolean }) {
  const tools = [
    [FileText, "Memorial SIGEF"],
    [FileText, "Utilitário ONR"],
    [FileText, "Reconstituição"],
    [Layers3, "Cena 3D"],
  ] as const;

  return (
    <div
      className={cn(
        "relative overflow-hidden bg-background",
        compact ? "h-full w-full" : "aspect-[2.1]",
      )}
    >
      <div className="absolute inset-0 grid grid-cols-[78px_1fr] sm:grid-cols-[126px_1fr]">
        <aside className="border-r border-border bg-card p-2 sm:p-3">
          <div className="flex items-center gap-1.5 border-b border-border pb-3">
            <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Cloud className="size-3.5" />
            </div>
            <span className="hidden truncate text-[0.55rem] font-semibold sm:block">
              Solution Softwares
            </span>
          </div>
          <div className="mt-3 space-y-1.5">
            {["Aplicativos", "Arquivos", "Workspaces"].map((item, index) => (
              <div
                key={item}
                className={cn(
                  "rounded-md px-2 py-1.5 text-[0.55rem]",
                  index === 0
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground",
                )}
              >
                {item}
              </div>
            ))}
          </div>
        </aside>
        <div className="min-w-0 overflow-hidden">
          <div className="flex h-9 items-center justify-between border-b border-border bg-accent/60 px-3">
            <span className="text-[0.65rem] font-semibold">Aplicativos</span>
            <span className="rounded-md border border-border px-2 py-1 text-[0.5rem] text-muted-foreground">
              José dos Santos
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 p-3">
            {tools.map(([Icon, title]) => (
              <div
                key={title}
                className="overflow-hidden rounded-md border border-border bg-card"
              >
                <div className="flex aspect-[2.2] items-center justify-center bg-accent">
                  <Icon className="size-5 text-foreground" />
                </div>
                <div className="p-2">
                  <p className="truncate text-[0.55rem] font-semibold">
                    {title}
                  </p>
                  <p className="mt-1 truncate text-[0.45rem] text-muted-foreground">
                    Abrir aplicação
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function RelationshipPanel({
  icon: Icon,
  title,
  description,
  product = "platform",
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  product?: "v2" | "platform";
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-6 md:p-8">
      <div
        className={cn(
          "flex size-10 items-center justify-center rounded-md border",
          product === "v2"
            ? "product-v2-gradient"
            : "border-primary bg-primary text-primary-foreground",
        )}
      >
        <Icon className="size-5" />
      </div>
      <h3 className="mt-6 text-lg font-semibold">{title}</h3>
      <p className="mt-2 max-w-sm text-sm leading-6 text-muted-foreground">
        {description}
      </p>
    </div>
  );
}

function ActionCard({
  icon: Icon,
  eyebrow,
  title,
  description,
  href,
  cta,
  product = "platform",
}: {
  icon: LucideIcon;
  eyebrow: string;
  title: string;
  description: string;
  href: string;
  cta: string;
  product?: "v2" | "platform";
}) {
  return (
    <Card className="h-full py-6">
      <CardHeader>
        <div
          className={cn(
            "mb-3 flex size-10 items-center justify-center rounded-md border",
            product === "v2"
              ? "product-v2-gradient"
              : "border-primary bg-primary text-primary-foreground",
          )}
        >
          <Icon className="size-5" />
        </div>
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          {eyebrow}
        </p>
        <CardTitle className="text-lg">{title}</CardTitle>
        <CardDescription className="leading-6">{description}</CardDescription>
      </CardHeader>
      <CardFooter className="mt-auto">
        <Button
          size="lg"
          className={cn(
            MARKETING_CTA_CLASS,
            "w-full cursor-pointer",
            product === "v2" &&
              "product-v2-action product-v2-gradient text-product-v2-foreground",
          )}
          asChild
        >
          <Link href={href}>
            {cta}
            <ArrowRight />
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
}

function OfferCard({
  icon: Icon,
  title,
  badge,
  description,
  cta,
  href,
  variant = "default",
}: {
  icon: LucideIcon;
  title: string;
  badge: string;
  description: string;
  cta: string;
  href: string;
  variant?: "default" | "outline";
}) {
  const featured = variant === "default";

  return (
    <Card
      className={cn(
        "product-v2-card h-full py-6",
        featured && "ring-2 ring-product-v2",
      )}
    >
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="product-v2-gradient flex size-9 items-center justify-center rounded-md border">
            <Icon className="size-5" />
          </div>
          <Badge
            variant={featured ? "default" : "outline"}
            className="product-v2-gradient"
          >
            {badge}
          </Badge>
        </div>
        <CardTitle className="mt-4">{title}</CardTitle>
        <CardDescription className="leading-6">{description}</CardDescription>
      </CardHeader>
      <CardFooter>
        <Button
          size="lg"
          variant={variant}
          className={cn(
            MARKETING_CTA_CLASS,
            "w-full cursor-pointer",
            featured
              ? "product-v2-action product-v2-gradient text-product-v2-foreground"
              : "product-v2-outline",
          )}
          asChild
        >
          <Link href={href}>
            {cta}
            <ArrowRight />
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
}

function ModuleCard({
  index,
  name,
  included,
  description,
  details,
}: {
  index: number;
  name: string;
  included?: string;
  description: string;
  details: readonly string[];
}) {
  return (
    <Card className="relative h-full overflow-hidden py-6">
      <div className="product-v2-gradient absolute right-5 top-5 flex size-8 items-center justify-center rounded-full font-mono text-xs font-semibold">
        M{index}
      </div>
      <CardHeader>
        <CardTitle className="pr-8 text-lg">{name}</CardTitle>
        {included ? (
          <p className="font-mono text-xs font-semibold text-product-v2">
            {included}
          </p>
        ) : null}
        <CardDescription className="leading-6">{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <FeatureList features={details} product="v2" />
      </CardContent>
    </Card>
  );
}

function PlatformJourney({
  icon: Icon,
  step,
  title,
  description,
}: {
  icon: LucideIcon;
  step: string;
  title: string;
  description: string;
}) {
  return (
    <Card className="h-full py-6">
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div className="flex size-10 items-center justify-center rounded-md border border-primary bg-primary text-primary-foreground">
            <Icon className="size-5" />
          </div>
          <span className="font-mono text-xs font-semibold text-muted-foreground">
            {step}
          </span>
        </div>
        <CardTitle className="mt-4 text-lg">{title}</CardTitle>
        <CardDescription className="leading-6">{description}</CardDescription>
      </CardHeader>
    </Card>
  );
}

type PlatformBentoTool = {
  icon: LucideIcon;
  title: string;
  description: string;
  result: string;
  image: string | null;
  imageAlt: string;
};

function PlatformToolsBento() {
  const tools: readonly [
    PlatformBentoTool,
    PlatformBentoTool,
    PlatformBentoTool,
    PlatformBentoTool,
    PlatformBentoTool,
    PlatformBentoTool,
  ] = [
    {
      icon: FileText,
      title: "Memorial SIGEF",
      description: "Transforme dados do trabalho em memoriais organizados.",
      result: "Documento técnico pronto para revisar e usar no seu fluxo.",
      image: "/images/memorial_incra.webp",
      imageAlt: "Memorial descritivo aberto no GeoOffice V2",
    },
    {
      icon: MapPinned,
      title: "Utilitário ONR",
      description: "Prepare coordenadas para os fluxos do RI Digital.",
      result: "Dados formatados com mais consistência antes do envio.",
      image: null,
      imageAlt: "",
    },
    {
      icon: Workflow,
      title: "Reconstituição",
      description: "Reconstrua áreas e perímetros a partir de memoriais.",
      result: "Geometria visual para conferir o perímetro reconstruído.",
      image: null,
      imageAlt: "",
    },
    {
      icon: Layers3,
      title: "Cena 3D",
      description:
        "Explore no navegador as cenas técnicas geradas no GeoOffice V2.",
      result:
        "Camadas, relevo, corte, aterro e resultados em uma leitura visual.",
      image: "/images/cena_3d.webp",
      imageAlt: "Modelo tridimensional produzido no GeoOffice V2",
    },
    {
      icon: Monitor,
      title: "Visualizador DWG/DXF",
      description:
        "Abra e confira desenhos técnicos diretamente no navegador, sem instalação local.",
      result: "Visualização técnica acessível para revisão e compartilhamento.",
      image: null,
      imageAlt: "",
    },
    {
      icon: Users,
      title: "Workspaces",
      description:
        "Separe projetos, equipes e clientes em espaços de trabalho organizados.",
      result: "Contexto, pessoas e arquivos reunidos para cada projeto.",
      image: null,
      imageAlt: "",
    },
  ];

  const [memorial, onr, reconstitution, scene3d, dwgViewer, workspaces] = tools;

  return (
    <div className="mt-10 grid gap-4 md:grid-cols-2">
      <div className="grid items-start gap-4 sm:grid-cols-2">
        <PlatformBentoCard tool={memorial} className="sm:col-span-2" />
        <PlatformBentoCard tool={dwgViewer} />
        <PlatformBentoCard tool={workspaces} />
      </div>
      <div className="grid items-start gap-4 sm:grid-cols-2">
        <PlatformBentoCard tool={onr} />
        <PlatformBentoCard tool={reconstitution} />
        <PlatformBentoCard tool={scene3d} className="sm:col-span-2" />
      </div>
    </div>
  );
}

function PlatformBentoCard({
  tool,
  className,
}: {
  tool: PlatformBentoTool;
  className?: string;
}) {
  const { icon: Icon, title, description, result, image, imageAlt } = tool;

  return (
    <Card
      className={cn(
        "group h-full transition-[transform,border-color,background-color] duration-300 hover:-translate-y-1 hover:border-primary/60 hover:bg-accent/40",
        image ? "py-0" : "py-6",
        className,
      )}
    >
      {image ? (
        <div className="relative aspect-[16/7] overflow-hidden bg-muted">
          <Image
            src={image}
            alt={imageAlt}
            fill
            sizes="(max-width: 768px) 100vw, 50vw"
            className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </div>
      ) : null}
      <PlatformBentoCopy
        className={image ? "py-6" : undefined}
        Icon={Icon}
        title={title}
        description={description}
        result={result}
      />
    </Card>
  );
}

function PlatformBentoCopy({
  className,
  Icon,
  title,
  description,
  result,
}: {
  className?: string;
  Icon: LucideIcon;
  title: string;
  description: string;
  result: string;
}) {
  return (
    <div className={cn("flex min-w-0 flex-col", className)}>
      <CardHeader>
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground transition-transform duration-300 group-hover:scale-105">
            <Icon className="size-5" />
          </div>
          <CardTitle className="min-w-0 text-lg">{title}</CardTitle>
        </div>
        <CardDescription className="leading-6">{description}</CardDescription>
      </CardHeader>
      <CardContent className="mt-auto">
        <p className="text-sm leading-6 text-foreground">{result}</p>
      </CardContent>
    </div>
  );
}

function Viewer3DShowcase() {
  const capabilities = [
    "Terreno anterior, terreno posterior, corte e aterro em camadas independentes.",
    "Superfície, malha e pontos com cor e transparência ajustáveis.",
    "Iluminação e exagero vertical para evidenciar o relevo.",
    "Volumes e áreas calculados pelo GeoOffice junto da cena.",
  ] as const;

  const flow = [
    ["01", "Gere no V2", "O cálculo técnico produz o arquivo da cena 3D."],
    [
      "02",
      "Envie à Platform",
      "Guarde a cena nos seus arquivos ou em um workspace.",
    ],
    [
      "03",
      "Explore online",
      "Abra, enquadre e controle a visualização no navegador.",
    ],
  ] as const;

  return (
    <section
      id="viewer-3d"
      className="scroll-mt-16 overflow-hidden border-b border-border"
      aria-labelledby="viewer-3d-title"
    >
      <div className="mx-auto w-full max-w-7xl px-4 py-20 md:px-8 md:py-24 lg:px-10">
        <div className="grid gap-12 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
          <Reveal>
            <Viewer3DMedia
              videoSrc={VIEWER_3D_VIDEO_SRC}
              posterSrc={VIEWER_3D_POSTER_SRC}
            />
          </Reveal>
          <Reveal delay={0.08}>
            <p className="mb-3 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-foreground/80">
              Viewer 3D
            </p>
            <h2
              id="viewer-3d-title"
              className="cn-font-heading text-3xl font-semibold tracking-[-0.035em] md:text-4xl"
            >
              Seu cálculo técnico ganha relevo no navegador.
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-7 text-muted-foreground">
              Gere a cena no GeoOffice V2 e explore o resultado na Platform.
              Visualize terrenos, corte e aterro, controle cada camada e
              consulte os valores calculados pelo GeoOffice no mesmo ambiente.
            </p>
            <ul className="mt-7 space-y-3">
              {capabilities.map((capability) => (
                <li
                  key={capability}
                  className="flex items-start gap-2.5 text-sm leading-6"
                >
                  <Check className="mt-1 size-4 shrink-0 text-primary" />
                  <span>{capability}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button
                size="lg"
                className={cn(
                  MARKETING_CTA_CLASS,
                  "w-full cursor-pointer sm:w-auto",
                )}
                asChild
              >
                <Link href="/auth/signup?product=platform">
                  Criar conta na Platform
                  <ArrowRight />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className={cn(
                  MARKETING_CTA_CLASS,
                  "w-full cursor-pointer sm:w-auto",
                )}
                asChild
              >
                <Link href="/desktop">Conhecer o GeoOffice V2</Link>
              </Button>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.12} className="mt-12">
          <ol className="grid border-y border-border md:grid-cols-3">
            {flow.map(([step, title, description], index) => (
              <li
                key={step}
                className={cn(
                  "grid grid-cols-[auto_1fr] gap-4 px-1 py-6 md:px-6",
                  index > 0 &&
                    "border-t border-border md:border-t-0 md:border-l",
                )}
              >
                <span className="font-mono text-xs font-semibold text-primary">
                  {step}
                </span>
                <div>
                  <h3 className="text-sm font-semibold">{title}</h3>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    {description}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </Reveal>
      </div>
    </section>
  );
}

function Viewer3DMedia({
  videoSrc,
  posterSrc,
}: {
  videoSrc: string | null;
  posterSrc: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);

  const togglePlayback = () => {
    const video = videoRef.current;

    if (!video) return;

    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  };

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card shadow-xl shadow-primary/10">
      <div className="flex h-11 items-center justify-between border-b border-border px-4">
        <div className="flex items-center gap-2">
          <span className="size-2 rounded-full bg-success" />
          <span className="font-mono text-[0.625rem] uppercase tracking-[0.14em] text-muted-foreground">
            GeoOffice Platform / Viewer 3D
          </span>
        </div>
        <Badge variant="outline">Gerado no V2</Badge>
      </div>

      <div className="relative aspect-[16/10] overflow-hidden bg-muted">
        {videoSrc ? (
          <>
            <video
              ref={videoRef}
              src={videoSrc}
              poster={posterSrc}
              autoPlay
              muted
              loop
              playsInline
              className="size-full object-cover motion-reduce:hidden"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              aria-label="Demonstração do Viewer 3D do GeoOffice Platform"
            />
            <Image
              src={posterSrc}
              alt="Viewer 3D exibindo uma cena técnica gerada no GeoOffice V2"
              fill
              sizes="(max-width: 1024px) 100vw, 60vw"
              className="hidden object-cover motion-reduce:block"
            />
            <Button
              type="button"
              size="icon"
              variant="secondary"
              className="absolute right-3 bottom-3 cursor-pointer motion-reduce:hidden"
              onClick={togglePlayback}
              aria-label={
                isPlaying ? "Pausar demonstração" : "Reproduzir demonstração"
              }
            >
              {isPlaying ? <Pause /> : <Play />}
            </Button>
          </>
        ) : (
          <Viewer3DFallback />
        )}
      </div>
    </div>
  );
}

function Viewer3DFallback() {
  return (
    <div className="grid size-full grid-cols-[1fr_8rem] sm:grid-cols-[1fr_12rem]">
      <div className="relative overflow-hidden bg-primary/5">
        <svg
          viewBox="0 0 720 460"
          className="absolute inset-0 size-full text-primary"
          aria-hidden="true"
        >
          <path
            d="M-40 360 110 210l125 48 128-160 145 107 160-96 95 91v220H-40Z"
            className="fill-primary/10 stroke-primary/30"
            strokeWidth="2"
          />
          <path
            d="m36 363 128-102 112 44 108-119 122 74 124-71 74 55"
            fill="none"
            className="stroke-primary/70"
            strokeWidth="3"
          />
          <g fill="none" className="stroke-primary/25" strokeWidth="1.5">
            <path d="m-10 394 174-133 112 44 108-119 122 74 161-94" />
            <path d="m-4 424 168-112 112 35 108-103 122 63 174-98" />
            <path d="M110 210 164 261 235 258 276 305 363 98 384 186 508 205 506 260 668 109 630 189" />
          </g>
        </svg>
        <div className="absolute top-4 left-4 flex flex-wrap gap-2">
          <Badge className="bg-primary text-primary-foreground">Aterro</Badge>
          <Badge variant="outline" className="bg-card/90">
            Corte
          </Badge>
        </div>
        <div className="absolute bottom-4 left-4 rounded-md border border-border bg-card/90 px-3 py-2 backdrop-blur-sm">
          <p className="font-mono text-[0.625rem] uppercase tracking-[0.12em] text-muted-foreground">
            Cena demonstrativa / georreferenciada
          </p>
          <p className="mt-1 text-xs font-semibold">Superfície + malha</p>
        </div>
      </div>
      <aside className="border-l border-border bg-card p-3 sm:p-4">
        <p className="text-xs font-semibold">Camadas</p>
        <div className="mt-3 space-y-2 text-[0.625rem] text-muted-foreground sm:text-xs">
          {["Terreno anterior", "Terreno posterior", "Corte", "Aterro"].map(
            (layer, index) => (
              <div key={layer} className="flex items-center gap-2">
                <span
                  className={cn(
                    "size-2 shrink-0 rounded-sm border border-primary",
                    index > 1 && "bg-primary",
                  )}
                />
                <span className="truncate">{layer}</span>
              </div>
            ),
          )}
        </div>
        <div className="mt-5 border-t border-border pt-4">
          <p className="text-xs font-semibold">Resultados</p>
          <dl className="mt-3 space-y-3">
            <div>
              <dt className="text-[0.55rem] text-muted-foreground sm:text-[0.625rem]">
                Volume de corte
              </dt>
              <dd className="mt-0.5 font-mono text-[0.625rem] font-semibold tabular-nums sm:text-xs">
                8,64 m³
              </dd>
            </div>
            <div>
              <dt className="text-[0.55rem] text-muted-foreground sm:text-[0.625rem]">
                Volume de aterro
              </dt>
              <dd className="mt-0.5 font-mono text-[0.625rem] font-semibold tabular-nums sm:text-xs">
                496,49 m³
              </dd>
            </div>
          </dl>
        </div>
      </aside>
      <p className="sr-only">
        Representação demonstrativa do Viewer 3D com camadas de terreno, corte,
        aterro e resultados técnicos.
      </p>
    </div>
  );
}

function WorkflowPoint({
  icon: Icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <div className="flex gap-3 border-b border-border pb-4 last:border-0 last:pb-0">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
        <Icon className="size-4" />
      </div>
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="mt-1 text-sm leading-6 text-muted-foreground">
          {description}
        </p>
      </div>
    </div>
  );
}

const formatMarketingPlanPrice = (
  plan: AccountPlanModel,
  billingCycle: PurchaseCycle,
) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: plan.currency.toUpperCase(),
    maximumFractionDigits: 2,
  }).format(billingCycle === "YEARLY" ? plan.priceYearly : plan.priceMonthly);

const getMarketingFeatureLabel = (feature: AccountPlanFeature) => {
  if (feature.type === "boolean") return feature.title;
  if (feature.type === "number" && feature.unlimited) {
    return `${feature.title}: Ilimitado`;
  }

  const unit = feature.unit?.trim().toUpperCase();
  const value =
    unit && unit !== "COUNT" ? `${feature.value} ${unit}` : feature.value;
  return `${feature.title}: ${value}`;
};

function PlatformPlanCard({
  plan,
  featured,
  billingCycle,
}: {
  plan: AccountPlanModel;
  featured?: boolean;
  billingCycle: PurchaseCycle;
}) {
  const displayedPrice = formatMarketingPlanPrice(plan, billingCycle);
  const displayedCadence =
    plan.planType === "FREE"
      ? "para começar"
      : billingCycle === "YEARLY"
        ? "por ano"
        : "por mês";
  const features = Object.entries(plan.features)
    .filter(
      ([, feature]) => feature.type !== "boolean" || feature.value === true,
    )
    .map(([, feature]) => getMarketingFeatureLabel(feature));

  return (
    <Card className={cn("h-full py-6", featured && "ring-2 ring-primary")}>
      <CardHeader>
        <div className="flex min-h-5 items-center justify-between gap-2">
          <span className="font-mono text-xs uppercase tracking-[0.16em] text-muted-foreground">
            {plan.name}
          </span>
          {featured ? <Badge>Recomendado</Badge> : null}
        </div>
        <CardTitle className="pt-4 font-mono text-3xl tabular-nums">
          {displayedPrice}
        </CardTitle>
        <CardDescription>{displayedCadence}</CardDescription>
        <p className="min-h-14 pt-4 text-sm leading-6 text-muted-foreground">
          {plan.description}
        </p>
      </CardHeader>
      <CardContent className="flex-1">
        <FeatureList features={features} />
      </CardContent>
      <CardFooter className="mt-auto">
        <Button
          variant={featured ? "default" : "outline"}
          size="lg"
          className={cn(MARKETING_CTA_CLASS, "w-full cursor-pointer")}
          asChild
        >
          <Link href={buildSubscribePath(plan.planType, billingCycle)}>
            {plan.planType === "FREE"
              ? "Criar conta grátis"
              : "Começar no GeoOffice Platform"}
            <ArrowRight />
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
}

function Capability({
  icon: Icon,
  title,
  description,
  product = "platform",
  included,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  product?: "v2" | "platform";
  included?: string;
}) {
  return (
    <div className="flex gap-3 border-t border-border pt-3">
      <div
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-md border",
          product === "v2"
            ? "product-v2-gradient"
            : "border-primary bg-primary text-primary-foreground",
        )}
      >
        <Icon className="size-4" />
      </div>
      <div>
        <p className="text-sm font-medium">{title}</p>
        {included ? (
          <p className="mt-0.5 font-mono text-[0.625rem] font-semibold text-product-v2">
            {included}
          </p>
        ) : null}
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          {description}
        </p>
      </div>
    </div>
  );
}

function RoadmapItem({
  icon: Icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-center gap-4 py-5">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-background">
        <Icon className="size-4 text-foreground" />
      </div>
      <div>
        <p className="text-sm font-semibold">{title}</p>
        <p className="mt-1 text-sm leading-6 text-muted-foreground">
          {description}
        </p>
      </div>
      <Badge variant="outline" className="ml-auto h-fit shrink-0">
        Em evolução
      </Badge>
    </div>
  );
}

function ProductBottomCta({ product }: { product: "v2" | "platform" }) {
  const isV2 = product === "v2";
  return (
    <section className="relative isolate overflow-hidden border-b border-border bg-muted/30">
      <div className="absolute inset-x-0 bottom-0 -z-10 h-64 bg-primary/5 blur-3xl" />
      <div className="mx-auto w-full max-w-7xl px-4 py-20 text-center md:px-8 md:py-28 lg:px-10">
        <Reveal className="mx-auto max-w-3xl">
          <Badge variant="outline">
            {isV2 ? "Comece pelo V2" : "Comece pelo GeoOffice Platform"}
          </Badge>
          <h2 className="cn-font-heading mt-6 text-3xl font-semibold tracking-[-0.035em] md:text-5xl">
            {isV2
              ? "Teste a precisão do GeoOffice V2 no seu próximo projeto."
              : "Organize seu primeiro projeto online hoje."}
          </h2>
          <p className="mt-5 text-base leading-7 text-muted-foreground">
            {isV2
              ? "Experimente o produto completo por 14 dias ou fale com um especialista sobre o módulo ideal."
              : "Crie uma conta gratuita e descubra as aplicações, arquivos e workspaces do GeoOffice Platform."}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button
              size="lg"
              className={cn(
                MARKETING_CTA_CLASS,
                "w-full cursor-pointer sm:w-auto",
                isV2 &&
                  "product-v2-action product-v2-gradient text-product-v2-foreground",
              )}
              asChild
            >
              {isV2 ? (
                <Link href={TRIAL_DESTINATION_PATH}>
                  Testar por 14 dias
                  <ArrowRight />
                </Link>
              ) : (
                <Link href="/auth/signup?product=platform">
                  Criar conta grátis
                  <ArrowRight />
                </Link>
              )}
            </Button>
            {isV2 ? (
              <Button
                size="lg"
                variant="outline"
                className={cn(
                  MARKETING_CTA_CLASS,
                  "w-full cursor-pointer sm:w-auto",
                )}
                asChild
              >
                <a href={WHATSAPP_URL}>Falar com especialista</a>
              </Button>
            ) : null}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FeatureList({
  features,
  product = "platform",
}: {
  features: readonly string[];
  product?: "v2" | "platform";
}) {
  return (
    <ul className="space-y-3 text-sm">
      {features.map((feature) => (
        <li key={feature} className="flex items-start gap-2.5">
          <Check
            className={cn(
              "mt-0.5 size-4 shrink-0",
              product === "v2" ? "text-product-v2" : "text-primary",
            )}
          />
          <span>{feature}</span>
        </li>
      ))}
    </ul>
  );
}

function SectionHeading({
  eyebrow,
  title,
  description,
  centered = false,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  centered?: boolean;
}) {
  return (
    <div className={cn(centered && "flex flex-col items-center")}>
      <p className="mb-3 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-foreground/80">
        {eyebrow}
      </p>
      <h2 className="cn-font-heading text-3xl font-semibold tracking-[-0.035em] md:text-4xl">
        {title}
      </h2>
      {description ? (
        <p
          className={cn(
            "mt-5 max-w-2xl text-base leading-7 text-muted-foreground",
            centered && "text-center",
          )}
        >
          {description}
        </p>
      ) : null}
    </div>
  );
}

function Reveal({
  children,
  className,
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-72px" }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function TopographicBackdrop() {
  return (
    <div className="pointer-events-none absolute inset-0 -z-10 text-primary">
      <div className="absolute inset-x-0 top-0 h-96 bg-primary/5 blur-3xl" />
      <svg
        viewBox="0 0 1440 760"
        className="h-full w-full opacity-[0.12]"
        aria-hidden="true"
        preserveAspectRatio="none"
      >
        <g fill="none" stroke="currentColor">
          <path d="M-60 560C170 370 290 660 520 450S870 600 1110 360s300-90 430-210" />
          <path d="M-70 600C160 410 300 700 540 490s350 150 590-90 310-80 430-180" />
          <path d="M-80 640C150 450 310 740 560 530s350 150 590-90 320-70 440-150" />
          <path d="M110 260c130-150 260 70 410-30s250 100 410-20 280 60 430-80" />
        </g>
      </svg>
    </div>
  );
}
